import { useCallback, useEffect, useState } from 'react';

// The server accepts 1–20 for a hole (scoring.py). The keypad offers exactly
// that range so a score can never be entered that the save would reject.
export const MAX_SCORE = 20;

// The second layer's scores. Everything a single key can't say.
export const TENS_SCORES = Array.from({ length: MAX_SCORE - 9 }, (_, i) => i + 10);

/**
 * Score entry for a phone-style keypad.
 *
 * EVERY key submits on one tap and the caller advances to the next player.
 * That is the whole point: a scorer taps 4 and the hole is done.
 *
 * The pad used to hold 1 and 2 for a second digit, since they could begin
 * 10–20 — but a 2 is a 2 essentially every time, and asking for a tick to
 * confirm it read as the pad not having registered the tap. So 10–20 moved
 * behind a "10 or more" key that swaps the grid for those scores: the rare
 * score pays the extra tap instead of the common one, and nothing is ever
 * half-entered.
 *
 * `resetKey` closes that second layer when the pad moves to another player or
 * hole, so it never opens onto a card the scorer didn't choose it for.
 */
export default function useScoreEntry(onSubmit, resetKey) {
  const [showTens, setShowTens] = useState(false);

  useEffect(() => { setShowTens(false); }, [resetKey]);

  const submit = useCallback((value) => {
    if (!Number.isInteger(value) || value < 1 || value > MAX_SCORE) return;
    setShowTens(false);
    onSubmit(value);
  }, [onSubmit]);

  const openTens = useCallback(() => setShowTens(true), []);
  const closeTens = useCallback(() => setShowTens(false), []);

  return { showTens, submit, openTens, closeTens };
}
